import { useCallback, useEffect, useState } from "react";
import { Check, RotateCcw, X } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { ArchitectRow } from "@/lib/domain";

const TEXT = {
  it: {
    kicker: "Account architetti",
    title: "Approvazioni",
    intro:
      "Il toolkit tecnico è riservato agli studi approvati. Approvando un account, l'architetto può scaricare modelli, capitolati e richiedere campioni.",
    pending: "In attesa",
    active: "Attivi",
    suspended: "Sospesi",
    all: "Tutti",
    approve: "Approva",
    suspend: "Sospendi",
    reinstate: "Riattiva",
    studio: "Studio",
    vat: "P.IVA",
    address: "Indirizzo",
    requestedAt: "Richiesto il",
    approvedAt: "Approvato il",
    none: "Nessuna richiesta.",
    empty: "Nessun account.",
  },
  en: {
    kicker: "Architect accounts",
    title: "Approvals",
    intro:
      "The technical toolkit is limited to approved studios. Approving an account lets the architect download models and specifications, and request samples.",
    pending: "Pending",
    active: "Active",
    suspended: "Suspended",
    all: "All",
    approve: "Approve",
    suspend: "Suspend",
    reinstate: "Reinstate",
    studio: "Studio",
    vat: "VAT",
    address: "Address",
    requestedAt: "Requested",
    approvedAt: "Approved",
    none: "No pending requests.",
    empty: "No accounts.",
  },
  es: {
    kicker: "Cuentas de arquitectos",
    title: "Aprobaciones",
    intro:
      "El toolkit técnico está limitado a estudios aprobados. Aprobar una cuenta permite al arquitecto descargar modelos y pliegos, y solicitar muestras.",
    pending: "Pendientes",
    active: "Activos",
    suspended: "Suspendidos",
    all: "Todos",
    approve: "Aprobar",
    suspend: "Suspender",
    reinstate: "Reactivar",
    studio: "Estudio",
    vat: "NIF",
    address: "Dirección",
    requestedAt: "Solicitado",
    approvedAt: "Aprobado",
    none: "Ninguna solicitud pendiente.",
    empty: "Ninguna cuenta.",
  },
} as const;

type Filter = "pending" | "active" | "suspended" | "all";

export default function ArchitectsPage() {
  const { locale } = useLocale();
  const tx = TEXT[locale];
  const [filter, setFilter] = useState<Filter>("pending");
  const [rows, setRows] = useState<ArchitectRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await api.architects(filter === "all" ? undefined : filter));
    } catch {
      setRows([]);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: string, status: "active" | "suspended" | "pending") {
    setBusyId(id);
    try {
      await api.decideArchitect(id, status);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  const filters: Array<{ id: Filter; label: string }> = [
    { id: "pending", label: tx.pending },
    { id: "active", label: tx.active },
    { id: "suspended", label: tx.suspended },
    { id: "all", label: tx.all },
  ];

  return (
    <AppShell>
      <div className="brand-kicker">{tx.kicker}</div>
      <h1 className="font-serif text-2xl text-paper sm:text-3xl">{tx.title}</h1>
      <p className="mt-2 max-w-2xl text-[0.78rem] leading-relaxed text-ice/65">
        {tx.intro}
      </p>

      <div className="mt-5 flex gap-1 rounded-full border border-white/15 p-0.5">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              "rounded-full px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] transition",
              filter === f.id ? "bg-white/15 text-paper" : "text-ice/60",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-3">
        {rows === null && <p className="text-sm text-ice/60">…</p>}
        {rows?.length === 0 && (
          <p className="text-sm text-ice/60">
            {filter === "pending" ? tx.none : tx.empty}
          </p>
        )}

        {rows?.map((a) => (
          <article key={a.id} className="glass-panel-strong p-4">
            <div className="flex flex-wrap items-start gap-3">
              <span
                className={cn(
                  "mt-0.5 rounded-full border px-2 py-0.5 text-[0.55rem] font-semibold uppercase tracking-[0.14em]",
                  a.status === "active"
                    ? "border-green/50 text-green"
                    : a.status === "pending"
                      ? "border-gold/50 text-goldbright"
                      : "border-rose/50 text-pink",
                )}
              >
                {a.status === "active"
                  ? tx.active
                  : a.status === "pending"
                    ? tx.pending
                    : tx.suspended}
              </span>

              <div className="min-w-0 flex-1">
                <div className="font-serif text-[0.98rem] text-paper">{a.name}</div>
                <div className="text-[0.72rem] text-ice/70">
                  {a.email}
                  {a.phone ? ` · ${a.phone}` : ""}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[0.66rem] text-ice/55">
                  {a.architect?.studioName && (
                    <span>
                      {tx.studio}: {a.architect.studioName}
                    </span>
                  )}
                  {a.architect?.vatNumber && (
                    <span className="num">
                      {tx.vat}: {a.architect.vatNumber}
                    </span>
                  )}
                  {a.architect?.address && <span>{a.architect.address}</span>}
                  <span className="num">
                    {tx.requestedAt}: {a.createdAt.slice(0, 10)}
                  </span>
                  {a.approvedAt && (
                    <span className="num">
                      {tx.approvedAt}: {a.approvedAt.slice(0, 10)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {a.status !== "active" && (
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => decide(a.id, "active")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-green/50 bg-green/10 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-green disabled:opacity-50"
                  >
                    <Check className="h-3 w-3" />
                    {a.status === "suspended" ? tx.reinstate : tx.approve}
                  </button>
                )}
                {a.status !== "suspended" && (
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => decide(a.id, "suspended")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-rose/50 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-pink disabled:opacity-50"
                  >
                    <X className="h-3 w-3" />
                    {tx.suspend}
                  </button>
                )}
                {a.status === "active" && (
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => decide(a.id, "pending")}
                    className="rounded-full border border-white/15 px-3 py-1.5 text-ice/60"
                    title={tx.pending}
                  >
                    <RotateCcw className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </AppShell>
  );
}
